import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowRight, Loader2, Flame, Activity, Lightbulb, FlaskConical, LayoutList, AlertTriangle, TrendingUp, Users, School as SchoolIcon, CheckCircle2, Clock, Calendar, Sparkles } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import { api } from '../api';

const TABS = [
  { id: 'priorities', label: 'أولويات اليوم', icon: Flame },
  { id: 'operations', label: 'مركز العمليات', icon: Activity },
  { id: 'insights',   label: 'تحليلات ذكية', icon: Lightbulb },
  { id: 'whatif',     label: 'ماذا-لو', icon: FlaskConical },
  { id: 'summary',    label: 'الملخص التنفيذي', icon: LayoutList },
];

function Card({ title, subtitle, children }) {
  return (
    <div className="panel p-4">
      {(title || subtitle) && (
        <div className="panel-header">
          <div>
            {title && <div className="panel-title">{title}</div>}
            {subtitle && <div className="panel-subtitle mt-0.5">{subtitle}</div>}
          </div>
        </div>
      )}
      {children}
    </div>
  );
}

function HealthBadge({ value }) {
  const v = value ?? 0;
  const c = v >= 80 ? 'pill-green' : v >= 60 ? 'pill-blue' : v >= 40 ? 'pill-amber' : 'pill-red';
  const l = v >= 80 ? 'ممتاز' : v >= 60 ? 'جيد' : v >= 40 ? 'يحتاج انتباه' : 'حرج';
  return <span className={`pill ${c} font-bold`}><span className="tabular-nums">{v}</span><span className="opacity-70 mr-1">{l}</span></span>;
}

function StatCard({ label, value, icon: Icon, tint, unit }) {
  return (
    <div className="panel px-3 py-3 flex items-center gap-2">
      <div className="icon-chip shrink-0" style={{ background: `${tint}18`, color: tint, border: `1px solid ${tint}30`, width: 34, height: 34 }}>
        <Icon size={16} />
      </div>
      <div>
        <div className="text-slate-400 text-[10.5px] font-semibold">{label}</div>
        <div className="text-white text-[19px] font-extrabold tabular-nums leading-tight">{value}</div>
        {unit && <div className="text-slate-500 text-[10px]">{unit}</div>}
      </div>
    </div>
  );
}

/* Simple frontend-only what-if math */
function computeWhatIf(base, teamPct, contractorTargetPct) {
  const boost = 1 + (teamPct / 100);
  const targetBoost = 1 + (contractorTargetPct / 200);
  const newCompletion = Math.min(100, Math.round((base.completionRate || 0) * boost * targetBoost * 10) / 10);
  const openReduction = Math.round((base.openTickets || 0) * (1 - 1 / boost));
  const delayedReduction = Math.round((base.delayedSchools || 0) * (1 - 1 / boost));
  return {
    newCompletion,
    openReduction,
    delayedReduction,
    expectedOpen: Math.max(0, (base.openTickets || 0) - openReduction),
    expectedDelayed: Math.max(0, (base.delayedSchools || 0) - delayedReduction),
  };
}

export default function ExecutivePage() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const initialTab = params.get('tab') || 'priorities';
  const [tab, setTab] = useState(initialTab);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [teamPct, setTeamPct] = useState(20);
  const [targetPct, setTargetPct] = useState(10);

  useEffect(() => {
    let filters = {};
    try { const raw = localStorage.getItem('tbc_dashboard_filters_v2'); if (raw) filters = JSON.parse(raw); } catch (e) {}
    let alive = true;
    setLoading(true);
    api.post('/executive/data', { filters }).then(({ data }) => { if (alive) setData(data); }).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const setTabAndUrl = (id) => { setTab(id); setParams({ tab: id }); };

  const p = data?.priorities || [];
  const ops = data?.operations || {};
  const ins = data?.insights || [];
  const sum = data?.summary || {};
  const wi = useMemo(() => sum?.kpis ? computeWhatIf(sum.kpis, teamPct, targetPct) : null, [sum, teamPct, targetPct]);
  const fmt = (n) => (n ?? 0).toLocaleString('en-US');

  return (
    <div dir="rtl" className="min-h-screen bg-[#0A101C] flex">
      <Sidebar />
      <main className="flex-1 p-4 min-w-0">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-slate-400 text-[11px] font-medium">لوحة القيادة › المركز التنفيذي</div>
            <h1 className="text-white text-[22px] font-extrabold tracking-tight mt-0.5">المركز التنفيذي</h1>
            <div className="text-slate-400 text-[12px] mt-0.5">أولويات • عمليات • تحليلات • محاكاة • ملخص</div>
          </div>
          <button onClick={() => nav('/')} className="panel panel-hoverable px-3 py-2 flex items-center gap-2 text-slate-200 text-[12.5px] font-semibold">
            <ArrowRight size={14} /> العودة
          </button>
        </div>

        {/* Tabs */}
        <div className="panel p-1.5 mb-3 inline-flex gap-1 flex-wrap">
          {TABS.map((t) => {
            const Ic = t.icon;
            const active = tab === t.id;
            return (
              <button key={t.id} onClick={() => setTabAndUrl(t.id)}
                className={`px-3 py-2 rounded-lg text-[12.5px] font-semibold flex items-center gap-1.5 transition-colors ${active ? 'bg-blue-600 text-white shadow-md shadow-blue-500/20' : 'text-slate-300 hover:bg-white/5'}`}>
                <Ic size={13} /> {t.label}
              </button>
            );
          })}
        </div>

        {loading && (
          <div className="panel p-10 flex items-center justify-center gap-2 text-slate-300">
            <Loader2 size={16} className="animate-spin text-blue-400" /> جاري التحميل...
          </div>
        )}

        {!loading && data && (
          <>
            {/* PRIORITIES */}
            {tab === 'priorities' && (
              <Card title="أعلى 10 مدارس تحتاج تدخل فوري" subtitle="محسوبة تلقائياً من البيانات الحقيقية">
                <div className="overflow-x-auto">
                  <table className="w-full text-[12px] text-right">
                    <thead className="bg-white/[0.03] border-b border-white/[0.06]">
                      <tr className="text-slate-300">
                        <th className="py-2 px-2 font-semibold">#</th>
                        <th className="py-2 px-2 font-semibold">المدرسة</th>
                        <th className="py-2 px-2 font-semibold text-center">المنطقة</th>
                        <th className="py-2 px-2 font-semibold">المقاول</th>
                        <th className="py-2 px-2 font-semibold text-center">مفتوحة</th>
                        <th className="py-2 px-2 font-semibold text-center">متأخرة</th>
                        <th className="py-2 px-2 font-semibold text-center">صحة</th>
                        <th className="py-2 px-2 font-semibold">سبب الأولوية</th>
                        <th className="py-2 px-2 font-semibold">الإجراء الموصى به</th>
                      </tr>
                    </thead>
                    <tbody>
                      {p.map((r, i) => (
                        <tr key={r.id} onClick={() => nav(`/school/${encodeURIComponent(r.id)}`)}
                          className="border-b border-white/[0.03] hover:bg-white/[0.03] cursor-pointer">
                          <td className="py-2 px-2"><span className={`pill ${i < 3 ? 'pill-red' : i < 6 ? 'pill-amber' : 'pill-blue'} font-bold`}>{i + 1}</span></td>
                          <td className="py-2 px-2 text-slate-100 font-semibold truncate max-w-[260px]" title={r.name}>{r.name}</td>
                          <td className="py-2 px-2 text-slate-300 text-center">{r.region || '—'}</td>
                          <td className="py-2 px-2 text-slate-300 truncate max-w-[160px]" title={r.contractor}>{r.contractor}</td>
                          <td className="py-2 px-2 text-center"><span className={`pill tabular-nums ${r.open > 0 ? 'pill-amber' : ''}`}>{r.open}</span></td>
                          <td className="py-2 px-2 text-center"><span className={`pill tabular-nums ${r.delayed > 0 ? 'pill-red' : ''}`}>{r.delayed}</span></td>
                          <td className="py-2 px-2 text-center"><HealthBadge value={r.health_score} /></td>
                          <td className="py-2 px-2 text-slate-300 text-[11.5px]">{(r.reasons || []).join(' • ')}</td>
                          <td className="py-2 px-2 text-blue-300 text-[11.5px] font-semibold">{r.action}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}

            {/* OPERATIONS */}
            {tab === 'operations' && (
              <div className="grid grid-cols-4 gap-3">
                <StatCard label="مدارس حرجة" value={fmt(ops.critical_schools)} icon={AlertTriangle} tint="#EF4444" />
                <StatCard label="مدارس تحتاج زيارة" value={fmt(ops.schools_needing_visit)} icon={SchoolIcon} tint="#F59E0B" />
                <StatCard label="مقاولون دون الهدف" value={fmt(ops.below_target_contractors)} icon={Users} tint="#F59E0B" />
                <StatCard label="بلاغات حرجة مفتوحة" value={fmt(ops.high_priority_open)} icon={AlertTriangle} tint="#EF4444" />
                <StatCard label="بلاغات متأخرة (>30 يوم)" value={fmt(ops.delayed_tickets)} icon={Clock} tint="#F59E0B" />
                <StatCard label="أنجزت اليوم" value={fmt(ops.completed_today)} icon={CheckCircle2} tint="#22C55E" />
                <StatCard label="بلاغات جديدة اليوم" value={fmt(ops.created_today)} icon={Calendar} tint="#3B82F6" />
                <StatCard label="نسبة الإنجاز" value={`${sum?.kpis?.completionRate ?? 0}%`} icon={TrendingUp} tint="#22C55E" />
              </div>
            )}

            {/* INSIGHTS */}
            {tab === 'insights' && (
              <div className="grid grid-cols-2 gap-3">
                {ins.map((v, i) => (
                  <div key={i} className="panel p-4 flex items-center gap-3">
                    <div className="icon-chip shrink-0" style={{ background: `${v.tint}18`, color: v.tint, border: `1px solid ${v.tint}30`, width: 40, height: 40 }}>
                      <Sparkles size={17} />
                    </div>
                    <div className="min-w-0">
                      <div className="text-slate-400 text-[11px] font-semibold">{v.title}</div>
                      <div className="text-white text-[16px] font-extrabold truncate" title={v.value}>{v.value}</div>
                      <div className="text-slate-500 text-[11px] mt-0.5">{v.extra}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* WHAT-IF */}
            {tab === 'whatif' && (
              <div className="grid grid-cols-12 gap-3">
                <Card title="المحاكاة" subtitle="محاكاة على مستوى تشغيلي — لا تعدّل البيانات">
                  <div className="col-span-5 space-y-4">
                    <label className="block">
                      <span className="text-slate-300 text-[12px] font-semibold">زيادة فرق الصيانة: <b className="text-blue-300">{teamPct}%</b></span>
                      <input type="range" min="0" max="100" step="5" value={teamPct} onChange={(e) => setTeamPct(+e.target.value)} className="w-full accent-blue-500 mt-2" />
                    </label>
                    <label className="block">
                      <span className="text-slate-300 text-[12px] font-semibold">تحسين أداء المقاولين المتأخرين بنسبة: <b className="text-blue-300">{targetPct}%</b></span>
                      <input type="range" min="0" max="100" step="5" value={targetPct} onChange={(e) => setTargetPct(+e.target.value)} className="w-full accent-blue-500 mt-2" />
                    </label>
                    <div className="text-slate-500 text-[11px] leading-6">
                      المحاكاة تستند إلى المؤشرات الحالية: {sum?.kpis?.completionRate ?? 0}% إنجاز · {fmt(sum?.kpis?.openTickets)} بلاغ مفتوح · {fmt(sum?.kpis?.delayedSchools)} مدرسة متأخرة.
                    </div>
                  </div>
                </Card>
                <div className="col-span-7 grid grid-cols-3 gap-3">
                  <StatCard label="نسبة إنجاز متوقعة" value={`${wi?.newCompletion ?? 0}%`} icon={TrendingUp} tint="#22C55E" />
                  <StatCard label="تقليل البلاغات المفتوحة" value={fmt(wi?.openReduction)} icon={CheckCircle2} tint="#3B82F6" />
                  <StatCard label="تقليل المدارس المتأخرة" value={fmt(wi?.delayedReduction)} icon={SchoolIcon} tint="#3B82F6" />
                  <StatCard label="مفتوحة بعد التطبيق" value={fmt(wi?.expectedOpen)} icon={Clock} tint="#F59E0B" />
                  <StatCard label="متأخرة بعد التطبيق" value={fmt(wi?.expectedDelayed)} icon={AlertTriangle} tint="#F59E0B" />
                </div>
              </div>
            )}

            {/* SUMMARY */}
            {tab === 'summary' && (
              <div className="space-y-3">
                <div className="panel p-4 flex items-center gap-4">
                  <div className="icon-chip" style={{ background: `${sum.overall_status?.color}22`, color: sum.overall_status?.color, border: `1px solid ${sum.overall_status?.color}44`, width: 52, height: 52 }}>
                    <Activity size={22} />
                  </div>
                  <div>
                    <div className="text-slate-400 text-[12px] font-semibold">الحالة العامة</div>
                    <div className="text-[22px] font-extrabold tracking-tight" style={{ color: sum.overall_status?.color }}>{sum.overall_status?.label}</div>
                    <div className="text-slate-400 text-[11px]">مؤشر صحة متوسط للمدارس: <b className="text-white tabular-nums">{sum.avg_health_score}</b> / 100</div>
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-3">
                  <StatCard label="إجمالي المدارس" value={fmt(sum.kpis?.totalSchools)} icon={SchoolIcon} tint="#60A5FA" />
                  <StatCard label="إجمالي البلاغات" value={fmt(sum.kpis?.registeredTickets)} icon={TrendingUp} tint="#F59E0B" />
                  <StatCard label="المدارس المتأخرة" value={fmt(sum.kpis?.delayedSchools)} icon={AlertTriangle} tint="#EF4444" />
                  <StatCard label="نسبة الإنجاز" value={`${sum.kpis?.completionRate ?? 0}%`} icon={CheckCircle2} tint="#22C55E" />
                </div>

                <Card title="أولويات اليوم (Top 5)">
                  <ul className="space-y-1.5">
                    {p.slice(0, 5).map((r, i) => (
                      <li key={r.id} className="flex items-center gap-2 text-[12.5px]">
                        <span className="pill pill-red font-bold">{i + 1}</span>
                        <button onClick={() => nav(`/school/${encodeURIComponent(r.id)}`)} className="text-slate-100 hover:text-blue-300 font-semibold flex-1 text-right truncate">{r.name}</button>
                        <span className="text-slate-400 text-[11px]">{r.region}</span>
                        <HealthBadge value={r.health_score} />
                      </li>
                    ))}
                  </ul>
                </Card>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
